import type { PlanDefinition, PlanDefinitionGoal } from '@medplum/fhirtypes';

import {
  type CompiledPlan,
  type DecodeContext,
  buildPlanDefinitionBase,
  createActivityCompiler,
  decodeActivityActions,
  decodeSharedFields,
  fromCodeableConcept,
  fromTiming,
  isActivityAction,
  sectionCodeOf,
  sectionConcept,
  toCodeableConcept,
  toFhirGoal,
  toTiming,
} from './shared';
import type { ContinuousPlanSpec, ControlBandSpec } from './spec';

// Control bands compile to goals (goal.target.detailRange is FHIR's native
// "keep this measure in range"), tagged with a control-band category so the
// decode can split them from the shared clinical goals.
function bandToGoal(band: ControlBandSpec): PlanDefinitionGoal {
  const quantity = (value: number) => ({ value, unit: band.unit });
  return {
    category: sectionConcept('control-band', 'Control band'),
    description: { text: `Keep ${band.measure.text} in range` },
    target: [
      {
        measure: toCodeableConcept(band.measure),
        detailRange: {
          low: band.low !== undefined ? quantity(band.low) : undefined,
          high: band.high !== undefined ? quantity(band.high) : undefined,
        },
      },
    ],
  };
}

function goalToBand(goal: PlanDefinitionGoal): ControlBandSpec | undefined {
  const target = goal.target?.[0];
  const measure = fromCodeableConcept(target?.measure);
  if (!target || !measure) {
    return undefined;
  }
  return {
    measure,
    low: target.detailRange?.low?.value,
    high: target.detailRange?.high?.value,
    unit: target.detailRange?.low?.unit ?? target.detailRange?.high?.unit,
  };
}

function isBandGoal(goal: PlanDefinitionGoal): boolean {
  return (
    sectionCodeOf(goal.category ? [goal.category] : undefined) ===
    'control-band'
  );
}

export function continuousSpecToResources(
  spec: ContinuousPlanSpec,
): CompiledPlan {
  const activities = createActivityCompiler(spec.title);
  return {
    planDefinition: {
      ...buildPlanDefinitionBase(spec, 'continuous', [
        ...(spec.goals ?? []).map((g) => toFhirGoal(g)),
        ...spec.bands.map(bandToGoal),
      ]),
      action: [
        ...(spec.cadence
          ? [
              {
                id: 'monitoring',
                title: 'Monitoring cadence',
                code: [sectionConcept('cadence', 'Monitoring cadence')],
                timingTiming: toTiming({ cadence: spec.cadence }),
              },
            ]
          : []),
        ...(spec.reviewRule
          ? [
              {
                id: 'review-rule',
                title: 'Review rule',
                description: spec.reviewRule,
                code: [sectionConcept('review-rule', 'Review rule')],
              },
            ]
          : []),
        // Standing activities the patient performs on an ongoing basis, as
        // top-level tagged actions.
        ...(activities.compile(spec.activities, 'standing') ?? []),
      ],
    },
    activityDefinitions: activities.definitions(),
  };
}

export function continuousFhirToSpec(
  pd: PlanDefinition,
  ctx?: DecodeContext,
): ContinuousPlanSpec | null {
  const bands = (pd.goal ?? [])
    .filter(isBandGoal)
    .map(goalToBand)
    .filter((b): b is ControlBandSpec => b !== undefined);
  if (bands.length === 0) {
    return null;
  }
  const cadenceAction = (pd.action ?? []).find(
    (a) => sectionCodeOf(a.code) === 'cadence',
  );
  const reviewAction = (pd.action ?? []).find(
    (a) => sectionCodeOf(a.code) === 'review-rule',
  );
  return {
    shape: 'continuous',
    ...decodeSharedFields(pd, (g) => !isBandGoal(g)),
    bands,
    cadence: fromTiming(cadenceAction?.timingTiming).cadence,
    reviewRule: reviewAction?.description,
    activities: decodeActivityActions(
      (pd.action ?? []).filter(isActivityAction),
      ctx,
    ),
  };
}
